/**
 * ConfigSchemaView — read-only display of a ConfigField[] schema.
 *
 * Shows field key, type, label, required indicator, hint, default value,
 * and options (for select fields). Supports dark and light theme variants.
 */

import type { ConfigField } from '../../../../../src/shared/view-system/registries';
import { debugTheme } from './debugTheme';

export interface ConfigSchemaViewProps {
  fields: ConfigField[];
  theme?: 'dark' | 'light';
}

function formatDefault(val: unknown): string {
  if (val == null) return '';
  if (typeof val === 'string') return val;
  return JSON.stringify(val);
}

const themes = {
  dark: {
    wrapper: debugTheme.dark.bg,
    header: `${debugTheme.dark.textMuted} border-b ${debugTheme.dark.border}`,
    row: `border-b ${debugTheme.dark.border}/50`,
    key: debugTheme.dark.text,
    type: debugTheme.dark.textAccent2,
    label: debugTheme.dark.text,
    hint: debugTheme.dark.textMuted,
    defaultVal: debugTheme.dark.textAccent,
    options: 'text-nightshade-400',
    badge: 'bg-red-500/20 text-red-400',
    empty: debugTheme.dark.textMuted,
  },
  light: {
    wrapper: debugTheme.light.bgAlt,
    header: `${debugTheme.light.textMuted} border-b ${debugTheme.light.border}`,
    row: `border-b ${debugTheme.light.border}/60`,
    key: 'text-slate-800',
    type: debugTheme.light.textAccent2,
    label: debugTheme.light.textMuted,
    hint: 'text-slate-400',
    defaultVal: debugTheme.light.textAccent,
    options: 'text-slate-500',
    badge: 'bg-red-100 text-red-600',
    empty: 'text-slate-400',
  },
} as const;

export function ConfigSchemaView({ fields, theme = 'dark' }: ConfigSchemaViewProps) {
  const t = themes[theme];

  if (fields.length === 0) {
    return (
      <div className={`font-mono text-[11px] py-2 ${t.empty}`}>No config fields defined.</div>
    );
  }

  return (
    <table className={`w-full text-[11px] font-mono border-collapse ${t.wrapper}`}>
      <thead>
        <tr className={`text-left ${t.header}`}>
          <th className="py-1 pr-3">Key</th>
          <th className="py-1 pr-3">Type</th>
          <th className="py-1 pr-3">Label</th>
          <th className="py-1 pr-3">Req</th>
          <th className="py-1 pr-3">Default</th>
          <th className="py-1">Hint / Options</th>
        </tr>
      </thead>
      <tbody>
        {fields.map((field) => {
          const optionsStr = field.options
            ? field.options.map((o) => o.label).join(', ')
            : '';
          const defaultStr = formatDefault(field.defaultValue);

          return (
            <tr key={field.key} className={t.row}>
              <td className={`py-1 pr-3 ${t.key}`}>{field.key}</td>
              <td className={`py-1 pr-3 ${t.type}`}>{field.type}</td>
              <td className={`py-1 pr-3 ${t.label}`}>{field.label}</td>
              <td className="py-1 pr-3">
                {field.required && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${t.badge}`}>req</span>
                )}
              </td>
              <td className={`py-1 pr-3 ${t.defaultVal}`}>{defaultStr || <span className={t.empty}>&mdash;</span>}</td>
              <td className={`py-1 ${t.hint} max-w-[250px] truncate`}>
                {optionsStr ? (
                  <span className={t.options}>{optionsStr}</span>
                ) : (
                  field.hint || <span className={t.empty}>&mdash;</span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
